import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const [pendingProtection, setPendingProtection] = useState(null);
  const [protectionSubmitting, setProtectionSubmitting] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("gr_pending_protection");
      if (raw) { setPendingProtection(JSON.parse(raw)); sessionStorage.removeItem("gr_pending_protection"); }
    } catch (e) { /* ignore */ }
  }, []);

  const activateProtection = async () => {
    if (!pendingProtection) return;
    setProtectionSubmitting(true);
    try {
      const { data } = await api.post("/protection/contracts", { ...pendingProtection, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Schutzpaket konnte nicht gestartet werden. Du kannst es jederzeit unter /schutzpaket nachholen.");
      setProtectionSubmitting(false);
    }
  };

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          const { data: o } = await api.get(`/payments/order-by-session/${sessionId}`);
          if (cancelled) return;
          setOrder(o);
          setState("paid");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= POLL_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section data-testid="payment-success" className="relative z-10 bg-[#050505] py-20 lg:py-28 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        {state === "checking" && (
          <div data-testid="payment-success-checking" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <Loader2 size={40} className="animate-spin text-white mx-auto mb-6" />
            <h2 className="font-display text-3xl">Zahlung wird bestätigt…</h2>
            <p className="text-[#aaa] mt-3">Einen Moment bitte, wir prüfen deine Zahlung bei Stripe.</p>
          </div>
        )}

        {state === "paid" && order && (
          <div data-testid="payment-success-confirmed" className="glass rounded-3xl p-10 lg:p-14">
            <CheckCircle2 size={48} className="text-emerald-400 mb-6" />
            <h2 className="font-display text-4xl lg:text-5xl">Zahlung erfolgreich.</h2>
            <p className="text-[#aaa] mt-4 leading-relaxed">
              Danke, {order.first_name}! Deine {order.service_label} für dein {order.device} ist bezahlt und eingegangen.
              Sende dein Gerät gut verpackt an uns – wir reparieren es und schicken es versichert zurück.
            </p>

            <div className="mt-6 rounded-2xl border border-white/15 bg-white/5 p-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-[#888]">Deine Referenznummer</p>
                <p data-testid="payment-success-reference" className="font-mono text-2xl text-white mt-1">{order.reference}</p>
                <p className="text-xs text-[#888] mt-1">Damit kannst du deinen Reparaturstatus jederzeit verfolgen.</p>
              </div>
              <a href={`${API}/orders/${order.id}/label`} target="_blank" rel="noreferrer" data-testid="payment-success-label"
                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors">
                Versandetikett (PDF)
              </a>
            </div>

            {order.customer_number && (
              <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-300/80">Deine Kundennummer</p>
                <p data-testid="payment-success-customer-number" className="font-mono text-2xl text-emerald-300 mt-1">{order.customer_number}</p>
              </div>
            )}

            {pendingProtection && (
              <div data-testid="payment-success-protection-cta" className="mt-4 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-[#0d1410] to-[#0a0a0a] p-6">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={18} className="text-emerald-400" />
                  <p className="text-white font-medium">Schutzpaket für dein {pendingProtection.device} jetzt abschließen</p>
                </div>
                <p className="text-xs text-[#999] leading-relaxed mb-4">
                  Gilt ab der nächsten Reparatur. Letzter Schritt: Zahlungsmethode für die monatliche Abbuchung hinterlegen.
                </p>
                <button onClick={activateProtection} disabled={protectionSubmitting} data-testid="payment-success-protection-activate"
                  className="w-full inline-flex items-center justify-center rounded-full bg-emerald-400 text-[#050505] py-3 text-sm font-medium hover:bg-emerald-300 transition-colors disabled:opacity-50">
                  {protectionSubmitting ? "Wird weitergeleitet…" : "Jetzt abschließen"}
                </button>
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#888] mb-4">Versandadresse</p>
              <p className="text-white leading-relaxed">
                G-Repair<br />Gramoz Qorri<br />Lindenstr. 43<br />83451 Piding
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 flex justify-between text-sm">
                <span className="text-[#888]">Bezahlter Betrag</span>
                <span className="font-mono text-white text-lg">{order.total.toFixed(2)} €</span>
              </div>
            </div>

            <Link to="/" data-testid="payment-success-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
              Zurück zur Startseite
            </Link>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-success-pending" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <Loader2 size={40} className="text-amber-400 mx-auto mb-6" />
            <h2 className="font-display text-3xl">Zahlung wird noch verarbeitet.</h2>
            <p className="text-[#aaa] mt-3">Das kann bei manchen Zahlungsarten etwas dauern. Du bekommst eine Bestätigung per E-Mail, sobald die Zahlung eingegangen ist.</p>
            <Link to="/" data-testid="payment-success-pending-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
              Zurück zur Startseite
            </Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-success-error" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <XCircle size={40} className="text-red-400 mx-auto mb-6" />
            <h2 className="font-display text-3xl">Zahlungsstatus konnte nicht geladen werden.</h2>
            <p className="text-[#aaa] mt-3">Bitte kontaktiere uns, falls deine Zahlung abgebucht wurde, du aber keine Bestätigung erhältst.</p>
            <Link to="/" data-testid="payment-success-error-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
              Zurück zur Startseite
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
